import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import Header from './components/Header';
import GamePanel from './components/GamePanel';
import UserPanel from './components/UserPanel';
import { 
  getNetworkConfig, 
  getContracts, 
  HEAD_OR_TAILS_ABI, 
  ERC20_ABI 
} from './config';

function App() {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [usdtContract, setUsdtContract] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  
  const [currentCycle, setCurrentCycle] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [lastWinner, setLastWinner] = useState(null);
  const [minInvestment, setMinInvestment] = useState(ethers.BigNumber.from(0));
  const [maxInvestment, setMaxInvestment] = useState(ethers.BigNumber.from(0));
  
  const [userInfo, setUserInfo] = useState(null);
  const [usdtBalance, setUsdtBalance] = useState(ethers.BigNumber.from(0));
  const [userHeadInvestment, setUserHeadInvestment] = useState(ethers.BigNumber.from(0));
  const [userTailInvestment, setUserTailInvestment] = useState(ethers.BigNumber.from(0));

  const timerRef = useRef(null);
  const refreshRef = useRef(null);

  const networkConfig = getNetworkConfig();
  const contracts = getContracts();

  const switchNetwork = async () => {
    if (!window.ethereum) return false;
    
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: networkConfig.chainIdHex }]
      });
      return true;
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: networkConfig.chainIdHex,
              chainName: networkConfig.chainName,
              nativeCurrency: networkConfig.nativeCurrency,
              rpcUrls: networkConfig.rpcUrls,
              blockExplorerUrls: networkConfig.blockExplorerUrls
            }]
          });
          return true;
        } catch (addError) {
          console.error('Failed to add network:', addError);
          return false;
        }
      }
      console.error('Failed to switch network:', switchError);
      return false;
    }
  };

  const checkNetwork = async () => {
    if (!window.ethereum) return false;
    
    try {
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      if (chainId !== networkConfig.chainIdHex) {
        return await switchNetwork();
      }
      return true;
    } catch (err) {
      console.error('Error checking network:', err);
      return false;
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert('Please install a Web3 wallet like MetaMask or Trust Wallet');
      return;
    }

    setIsConnecting(true);

    try {
      const isCorrectNetwork = await checkNetwork();
      if (!isCorrectNetwork) {
        setIsConnecting(false);
        return;
      }

      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });

      if (accounts.length > 0) {
        const web3Provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
        const web3Signer = web3Provider.getSigner();
        
        const headOrTailsContract = new ethers.Contract(
          contracts.headOrTails,
          HEAD_OR_TAILS_ABI,
          web3Signer
        );

        const usdtTokenContract = new ethers.Contract(
          contracts.usdt,
          ERC20_ABI,
          web3Signer
        );

        setProvider(web3Provider);
        setSigner(web3Signer);
        setContract(headOrTailsContract);
        setUsdtContract(usdtTokenContract);
        setAccount(accounts[0]);
      }
    } catch (err) {
      console.error('Connection error:', err);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setContract(null);
    setUsdtContract(null);
    setIsRegistered(false);
    setUserInfo(null);
    setUsdtBalance(ethers.BigNumber.from(0));
    setUserHeadInvestment(ethers.BigNumber.from(0));
    setUserTailInvestment(ethers.BigNumber.from(0));
  };

  const loadContractData = async () => {
    if (!contract) return;

    try {
      const [cycle, minInv, maxInv, lastWin] = await Promise.all([
        contract.currentCycle(),
        contract.minInvestment(),
        contract.maxInvestment(),
        contract.getLastWinningRoom()
      ]);

      setCurrentCycle(cycle);
      setMinInvestment(minInv);
      setMaxInvestment(maxInv);
      setLastWinner({ room: lastWin.winningRoom, cycle: lastWin.cycle });

      const timeRemaining = await contract.getCurrentCycleTimeLeft();
      setTimeLeft(timeRemaining.toNumber());

    } catch (err) {
      console.error('Error loading contract data:', err);
    }
  };

  const loadUserData = async () => {
    if (!contract || !account || !usdtContract) return;

    try {
      const [registered, info, balance, cycle] = await Promise.all([
        contract.isUserRegistered(account),
        contract.getUserInfo(account),
        usdtContract.balanceOf(account),
        contract.currentCycle()
      ]);

      setIsRegistered(registered);
      setUserInfo({
        totalInvested: info.totalInvested,
        claimableWinnings: info.claimableWinnings,
        totalWithdrawn: info.totalWithdrawn
      });
      setUsdtBalance(balance);

      // Get user's investments for current cycle
      if (registered) {
        try {
          const investments = await contract.getUserInvestments(account, 0, 100);
          let headTotal = ethers.BigNumber.from(0);
          let tailTotal = ethers.BigNumber.from(0);

          for (let i = 0; i < investments.amounts.length; i++) {
            if (investments.cycleNums[i].eq(cycle)) {
              if (investments.rooms[i] === 0) {
                headTotal = headTotal.add(investments.amounts[i]);
              } else {
                tailTotal = tailTotal.add(investments.amounts[i]);
              }
            }
          }

          setUserHeadInvestment(headTotal);
          setUserTailInvestment(tailTotal);
        } catch (invErr) {
          console.error('Error loading investments:', invErr);
        }
      }
    } catch (err) {
      console.error('Error loading user data:', err);
    }
  };

  const refreshData = () => {
    loadContractData();
    loadUserData();
  };

  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else if (accounts[0] !== account) {
        setAccount(accounts[0]);
      }
    };

    const handleChainChanged = async (chainId) => {
      if (chainId !== networkConfig.chainIdHex) {
        await switchNetwork();
      }
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [account, networkConfig.chainIdHex]);

  useEffect(() => {
    if (contract) {
      loadContractData();
    }
  }, [contract]);

  useEffect(() => {
    if (contract && account) {
      loadUserData();
    }
  }, [contract, account]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeLeft]);

  useEffect(() => {
    if (contract) {
      refreshRef.current = setInterval(refreshData, 30000);
    }

    return () => {
      if (refreshRef.current) {
        clearInterval(refreshRef.current);
      }
    };
  }, [contract]);

  return (
    <div className="app">
      {/* Animated Background */}
      <div className="bg-container">
        <div className="bg-gradient"></div>
        <div className="floating-coins">
          <div className="float-coin c1">💰</div>
          <div className="float-coin c2">👑</div>
          <div className="float-coin c3">🎯</div>
          <div className="float-coin c4">💎</div>
          <div className="float-coin c5">🏆</div>
        </div>
      </div>

      <Header 
        account={account}
        onConnect={connectWallet}
        onDisconnect={disconnectWallet}
        isConnecting={isConnecting}
      />

      <main className="main">
        <div className="container">
          <GamePanel 
            contract={contract}
            usdtContract={usdtContract}
            account={account}
            timeLeft={timeLeft}
            currentCycle={currentCycle}
            lastWinner={lastWinner}
            minInvestment={minInvestment}
            maxInvestment={maxInvestment}
            userHeadInvestment={userHeadInvestment}
            userTailInvestment={userTailInvestment}
            onRefresh={refreshData}
            contractAddress={contracts.headOrTails}
            isRegistered={isRegistered}
          />

          <UserPanel 
            contract={contract}
            account={account}
            userInfo={userInfo}
            usdtBalance={usdtBalance}
            isRegistered={isRegistered}
            onRefresh={refreshData}
          />
        </div>
      </main>

      <footer className="footer">
        <p>© 2024 Head or Tails. Play responsibly.</p>
      </footer>
    </div>
  );
}

export default App;
